import { Box, Divider, type SxProps, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { SystemRoundedCheckbox } from "../../../../components/UIToolkit/Clickable/SystemRoundedCheckbox";
import { SystemDropdown } from "../../../../components/UIToolkit/Inputs/SystemDropDown";
import { smallLabelTypographySx } from "./consts";
import type { EmrMapping } from "./locationEmrMapping";

const regularFieldSx: SxProps = { width: "160px" } as const satisfies SxProps;

type Props = {
	availableUnits: string[];
	availableFloors: string[];
	availableRooms: string[];
	mapping: EmrMapping;
	departmentWideCensus: boolean;
	onDepartmentWideCensusChange: (checked: boolean) => void;
	/** Ratio only: the ladder needs an explicit choice, so the area fields lock while Department wide is ticked. */
	lockAreasWhenDepartmentWide: boolean;
	onMappingChange: (changes: Partial<EmrMapping>) => void;
	onDropdownOpen: () => void;
	onDropdownClose: () => void;
};

/**
 * Where a location's census comes from: an EMR area (unit, floor, rooms) or the whole department. The
 * two are exclusive. In a ratio department the area dropdowns are disabled while "Department wide census"
 * is checked; elsewhere they stay open and choosing an area unchecks it (see `censusChoiceAfterAreaChange`).
 */
export const LocationEmrMappingSection: FC<Props> = ({
	availableUnits,
	availableFloors,
	availableRooms,
	mapping,
	departmentWideCensus,
	onDepartmentWideCensusChange,
	lockAreasWhenDepartmentWide,
	onMappingChange,
	onDropdownOpen,
	onDropdownClose,
}) => {
	const { t } = useTranslation();
	const noneOption = { value: "", label: t("schedule.template.v3.editor.none") };
	const areasDisabled = lockAreasWhenDepartmentWide && departmentWideCensus;
	return (
		<>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 1, width: "100%", alignItems: "start" }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					<SystemRoundedCheckbox
						checked={departmentWideCensus}
						onChange={onDepartmentWideCensusChange}
						label={t("schedule.template.census.department.wide")}
						data-testid="location-department-wide-census-checkbox"
					/>
					<Typography sx={{ ...smallLabelTypographySx, maxWidth: "500px" }}>{t("schedule.template.census.department.wide.helper")}</Typography>
				</Box>
				{(availableUnits.length > 0 || availableFloors.length > 0) && (
					<Box sx={{ display: "flex", gap: 1, width: "100%" }}>
						{availableUnits.length > 0 && (
							<SystemDropdown
								onOpen={onDropdownOpen}
								onClose={onDropdownClose}
								label={t("schedule.template.v3.editor.unit")}
								selectSx={regularFieldSx}
								disabled={areasDisabled}
								items={[noneOption, ...availableUnits.map((unit) => ({ value: unit, label: unit }))]}
								selectedItem={mapping.unit ?? ""}
								onChange={(item) => onMappingChange({ unit: item === "" ? undefined : item })}
								data-testid="location-emr-unit-dropdown"
							/>
						)}
						{availableFloors.length > 0 && (
							<SystemDropdown
								onOpen={onDropdownOpen}
								onClose={onDropdownClose}
								label={t("schedule.template.v3.editor.floor")}
								selectSx={regularFieldSx}
								disabled={areasDisabled}
								items={[noneOption, ...availableFloors.map((floor) => ({ value: floor, label: floor }))]}
								selectedItem={mapping.floor ?? ""}
								onChange={(item) => onMappingChange({ floor: item === "" ? undefined : item })}
								data-testid="location-emr-floor-dropdown"
							/>
						)}
					</Box>
				)}
				{availableRooms.length > 0 && (
					<SystemDropdown
						multiple
						label={t("schedule.template.v3.editor.rooms")}
						formControlSx={{ width: "100%" }}
						disabled={areasDisabled}
						items={availableRooms.map((room) => ({ value: room, label: room }))}
						selectedItems={mapping.rooms}
						onChangeMultiple={(rooms) => onMappingChange({ rooms })}
						onOpen={onDropdownOpen}
						onClose={onDropdownClose}
						data-testid="location-emr-rooms-dropdown"
					/>
				)}
			</Box>
			<Divider flexItem />
		</>
	);
};
